import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 * @property {HTMLAnchorElement} [accountLink] - The account link, which opens the Rivo loyalty widget.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.refs.accountLink?.addEventListener('click', this.#onAccountLinkClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.refs.accountLink?.removeEventListener('click', this.#onAccountLinkClick);
  }

  /**
   * The account link points to `{routes.account_url}#rivo` so Rivo's loyalty
   * widget opens on click. With Shopify's new customer accounts, navigating to
   * `/account` redirects off-theme before Rivo's hashchange listener ever sees
   * the hash, so the widget never opens. When Rivo is active, open it in place
   * by updating the hash instead of navigating, which works for both classic
   * and new customer accounts.
   * @param {MouseEvent} event
   */
  #onAccountLinkClick = (event) => {
    if (!window.Rivo || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();

    if (window.location.hash === '#rivo') {
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } else {
      window.location.hash = 'rivo';
    }
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
